import React, { useState, useRef, useEffect } from 'react';
import { User, ChevronDown, X, Search } from 'lucide-react';

const SearchableUserDropdown = ({ 
  users, 
  value, 
  onChange, 
  placeholder = "All Users", 
  className = "",
  maxResults = 50 
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const dropdownRef = useRef(null);

  // Filter users based on search term
  const filteredUsers = (users || []).filter(user => {
    if (!searchTerm) return true;
    const searchLower = searchTerm.toLowerCase();
    return (
      user.name?.toLowerCase().includes(searchLower) ||
      user.role?.name?.toLowerCase().includes(searchLower) ||
      user.accountId?.toLowerCase().includes(searchLower) ||
      user.group?.name?.toLowerCase().includes(searchLower)
    );
  });

  // Limit results
  const displayUsers = filteredUsers.slice(0, maxResults);
  const hasMore = filteredUsers.length > maxResults;

  // Get selected user name
  const selectedUser = (users || []).find(u => u.id === value);
  const displayValue = selectedUser 
    ? `${selectedUser.name || 'Unknown'}${selectedUser.role?.name ? ` (${selectedUser.role.name})` : ''}${selectedUser.group?.name ? ` • ${selectedUser.group.name}` : ''}`
    : placeholder;

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm('');
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleSelect = (userId) => {
    onChange(userId);
    setIsOpen(false);
    setSearchTerm('');
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setSearchTerm('');
  };

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Dropdown Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between neo-field dark:text-dark-text px-3 py-2 min-w-[350px] text-left"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          <User size={16} className="text-gray-400 dark:text-dark-muted flex-shrink-0" />
          <span className="truncate text-sm">
            {displayValue}
          </span>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          {value && (
            <button
              type="button"
              onClick={handleClear}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-dark-text p-0.5"
            >
              <X size={14} />
            </button>
          )}
          <ChevronDown 
            size={16} 
            className={`text-gray-400 dark:text-dark-muted transition-transform ${isOpen ? 'rotate-180' : ''}`} 
          />
        </div>
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div className="absolute z-50 w-full mt-2 neo-raised max-h-80 overflow-hidden flex flex-col">
          {/* Search Input */}
          <div className="p-2">
            <div className="relative">
              <Search size={16} className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400 dark:text-dark-muted" />
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search users..."
                className="w-full pl-8 pr-3 py-2 text-sm neo-field dark:text-dark-text"
                autoFocus
                onClick={(e) => e.stopPropagation()}
              />
            </div>
          </div>

          {/* Options List */}
          <div className="overflow-y-auto flex-1">
            {/* "All Users" option */}
            <button
              type="button"
              onClick={() => handleSelect('')}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors ${
                !value ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' : 'text-gray-700 dark:text-dark-text'
              }`}
            >
              {placeholder}
            </button>

            {/* User Options */}
            {displayUsers.length > 0 ? (
              <>
                {displayUsers.map((user) => (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => handleSelect(user.id)}
                    className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-100 dark:hover:bg-slate-700/50 transition-colors ${
                      value === user.id 
                        ? 'bg-primary-50 dark:bg-primary-900/20 text-primary-600 dark:text-primary-400' 
                        : 'text-gray-700 dark:text-dark-text'
                    }`}
                  >
                    <div className="font-medium">{user.name}</div>
                    <div className="text-xs text-gray-500 dark:text-dark-muted">
                      {user.role?.name || ''} {user.accountId ? `• ${user.accountId}` : ''} {user.group?.name ? `• ${user.group.name}` : ''}
                    </div>
                  </button>
                ))}
                {hasMore && (
                  <div className="px-3 py-2 text-xs text-gray-500 dark:text-dark-muted text-center border-t border-gray-200 dark:border-dark-border">
                    Showing {maxResults} of {filteredUsers.length} users. Refine your search to see more.
                  </div>
                )}
              </>
            ) : (
              <div className="px-3 py-8 text-center text-sm text-gray-500 dark:text-dark-muted">
                No users found
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SearchableUserDropdown;

